import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'

/**
 * THROWAWAY PROOF-OF-CONCEPT (not production).
 *
 * Tests the CHOREOGRAPHY + TIMING of the "mother rocks crying baby to
 * sleep → tagline" narrative with framer-motion, using simple shapes.
 * It is deliberately abstract/crude — the goal is to feel the sequence,
 * not the final art. A real version needs an illustrated/Lottie scene.
 *
 * Route: /anim-poc (added to AppRouter as a dev preview; remove after review).
 */

type Stage =
  | 'crying'
  | 'approaching'
  | 'lifting'
  | 'sitting'
  | 'rocking'
  | 'calming'
  | 'sleeping'
  | 'tagline'

const SEQUENCE: ReadonlyArray<{ readonly stage: Stage; readonly hold: number }> = [
  { stage: 'crying', hold: 2600 },
  { stage: 'approaching', hold: 2400 },
  { stage: 'lifting', hold: 1500 },
  { stage: 'sitting', hold: 1600 },
  { stage: 'rocking', hold: 2800 },
  { stage: 'calming', hold: 1800 },
  { stage: 'sleeping', hold: 2600 },
  { stage: 'tagline', hold: 3400 }
]

const C = {
  bg: '#F8F5EE',
  mother: '#AD6043',
  motherDark: '#8f4d34',
  baby: '#F0E6D3',
  babyHead: '#E8C9A8',
  floor: '#E8DCC8',
  text: '#2C2416',
  tear: '#7FA8C9'
}

const STAGE_LABEL: Record<Stage, string> = {
  crying: 'Baby is crying on the floor',
  approaching: 'Mother walks over',
  lifting: 'She picks the baby up',
  sitting: 'She sits, legs crossed',
  rocking: 'Rocking on her lap',
  calming: 'The baby calms… and smiles',
  sleeping: 'Slowly drifts to sleep',
  tagline: '✨'
}

export function BabyRockPoc(): JSX.Element {
  const [stage, setStage] = useState<Stage>('crying')
  const [runId, setRunId] = useState(0)

  useEffect(() => {
    let cancelled = false
    const timers: ReturnType<typeof setTimeout>[] = []
    let acc = 0
    setStage('crying')
    for (const step of SEQUENCE) {
      const t = setTimeout(() => {
        if (!cancelled) setStage(step.stage)
      }, acc)
      timers.push(t)
      acc += step.hold
    }
    // auto-loop
    const loop = setTimeout(() => {
      if (!cancelled) setRunId((n) => n + 1)
    }, acc + 1200)
    timers.push(loop)
    return () => {
      cancelled = true
      timers.forEach(clearTimeout)
    }
  }, [runId])

  const standing = stage === 'crying' || stage === 'approaching' || stage === 'lifting'
  const rocking = stage === 'rocking' || stage === 'calming' || stage === 'sleeping' || stage === 'tagline'
  const onFloor = stage === 'crying' || stage === 'approaching'
  const crying = onFloor
  const asleep = stage === 'sleeping' || stage === 'tagline'
  const smiling = stage === 'calming' || asleep

  const motherX = stage === 'crying' ? -80 : stage === 'approaching' ? 120 : stage === 'lifting' ? 150 : 168
  const baby =
    onFloor ? { x: 205, y: 244 } : stage === 'lifting' ? { x: 178, y: 152 } : { x: 176, y: 196 }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: C.bg,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        color: C.text
      }}
    >
      <p style={{ fontSize: 13, letterSpacing: 1, opacity: 0.5, fontFamily: 'system-ui' }}>
        ROUGH framer-motion PROOF OF CONCEPT — shapes only
      </p>

      <svg width={360} height={300} viewBox="0 0 360 300" role="img" aria-label="Mother rocking baby to sleep">
        {/* floor */}
        <line x1={0} y1={250} x2={360} y2={250} stroke={C.floor} strokeWidth={3} />

        <motion.g
          animate={{ rotate: rocking ? [-4, 4, -4] : 0 }}
          transition={
            rocking
              ? { rotate: { repeat: Infinity, duration: 1.8, ease: 'easeInOut' } }
              : { rotate: { duration: 0.6, ease: 'easeOut' } }
          }
          style={{ transformOrigin: '172px 250px' }}
        >
          {/* ---------- mother (standing) ---------- */}
          <motion.g
            animate={{ x: motherX, opacity: standing ? 1 : 0 }}
            transition={{ x: { duration: 1.4, ease: 'easeInOut' }, opacity: { duration: 0.4 } }}
            style={{ translateY: 250 }}
          >
            <circle cx={0} cy={-120} r={15} fill={C.mother} />
            <path d="M -14 -104 L 14 -104 L 22 -4 L -22 -4 Z" fill={C.mother} />
          </motion.g>

          {/* ---------- mother (seated, legs crossed) ---------- */}
          <motion.g
            animate={{ opacity: standing ? 0 : 1 }}
            transition={{ opacity: { duration: 0.4 } }}
            style={{ translateX: 172, translateY: 250 }}
          >
            {/* crossed-leg base */}
            <path d="M -40 -6 L 40 -6 L 26 4 L -26 4 Z" fill={C.motherDark} />
            {/* torso */}
            <path d="M -15 -82 L 15 -82 L 22 -10 L -22 -10 Z" fill={C.mother} />
            {/* head */}
            <circle cx={0} cy={-98} r={15} fill={C.mother} />
            {/* cradling arm arc */}
            <path d="M -20 -44 Q 0 -28 22 -42" stroke={C.motherDark} strokeWidth={7} fill="none" strokeLinecap="round" />
          </motion.g>

          {/* ---------- baby ---------- */}
          <motion.g
            animate={{ x: baby.x, y: baby.y, rotate: crying ? [-3, 3, -3] : 0 }}
            transition={{
              x: { duration: 1, ease: 'easeOut' },
              y: { duration: 1, ease: 'easeOut' },
              rotate: crying ? { repeat: Infinity, duration: 0.5 } : { duration: 0.3 }
            }}
          >
            {/* swaddle body */}
            <ellipse cx={0} cy={2} rx={14} ry={10} fill={C.baby} />
            {/* head */}
            <circle cx={0} cy={-9} r={9} fill={C.babyHead} />
            {/* eyes */}
            {asleep ? (
              <>
                <path d="M -5 -10 q 2 2 4 0" stroke={C.text} strokeWidth={1.2} fill="none" />
                <path d="M 1 -10 q 2 2 4 0" stroke={C.text} strokeWidth={1.2} fill="none" />
              </>
            ) : (
              <>
                <circle cx={-3} cy={-10} r={1.3} fill={C.text} />
                <circle cx={3} cy={-10} r={1.3} fill={C.text} />
              </>
            )}
            {/* mouth */}
            {smiling ? (
              <path d="M -4 -5 q 4 4 8 0" stroke={C.text} strokeWidth={1.2} fill="none" strokeLinecap="round" />
            ) : (
              <path d="M -4 -4 q 4 -3 8 0" stroke={C.text} strokeWidth={1.2} fill="none" strokeLinecap="round" />
            )}
            {/* tears (only while crying) */}
            {crying ? (
              <>
                <motion.circle
                  cx={-3}
                  cy={-7}
                  r={1.6}
                  fill={C.tear}
                  animate={{ cy: [-7, 4], opacity: [0.9, 0] }}
                  transition={{ repeat: Infinity, duration: 1, ease: 'easeIn' }}
                />
                <motion.circle
                  cx={3}
                  cy={-7}
                  r={1.6}
                  fill={C.tear}
                  animate={{ cy: [-7, 4], opacity: [0.9, 0] }}
                  transition={{ repeat: Infinity, duration: 1, ease: 'easeIn', delay: 0.5 }}
                />
              </>
            ) : null}
            {/* Zzz (only while asleep) */}
            {asleep ? (
              <motion.text
                x={14}
                y={-16}
                fontSize={11}
                fill={C.motherDark}
                fontFamily="system-ui"
                animate={{ y: [-16, -28], opacity: [0, 1, 0] }}
                transition={{ repeat: Infinity, duration: 2.2, ease: 'easeOut' }}
              >
                z z z
              </motion.text>
            ) : null}
          </motion.g>
        </motion.g>
      </svg>

      {/* stage caption */}
      <motion.p
        key={stage}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        style={{ fontSize: 18, minHeight: 28 }}
      >
        {STAGE_LABEL[stage]}
      </motion.p>

      {/* tagline reveal */}
      <motion.div
        animate={{ opacity: stage === 'tagline' ? 1 : 0, y: stage === 'tagline' ? 0 : 10 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        style={{ textAlign: 'center' }}
      >
        <div style={{ fontSize: 30, color: C.mother, fontWeight: 600 }}>ಉಯ್ಯಮಡಿಲು</div>
        <div style={{ fontSize: 22, fontStyle: 'italic' }}>A Rocking Lap, Always There</div>
      </motion.div>

      <button
        type="button"
        onClick={() => setRunId((n) => n + 1)}
        style={{
          marginTop: 8,
          padding: '8px 18px',
          borderRadius: 999,
          border: 'none',
          background: C.mother,
          color: '#FDFAF5',
          fontFamily: 'system-ui',
          fontSize: 13,
          cursor: 'pointer'
        }}
      >
        Replay
      </button>
    </div>
  )
}
